﻿import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Settings, LogOut, MousePointer2, Minus, Circle, Square, Pentagon, Spline as SplineIcon, Type as TypeIcon, Ruler, Trash2, Layers, Magnet, Undo2, Redo2, Download, Eraser } from 'lucide-react';

const TOOLS = [
    { id: 'select', label: 'Select', icon: MousePointer2 },
    { id: 'line', label: 'Line', icon: Minus },
    { id: 'rect', label: 'Rectangle', icon: Square },
    { id: 'circle', label: 'Circle', icon: Circle },
    { id: 'poly', label: 'Polygon', icon: Pentagon },
    { id: 'text', label: 'Text', icon: TypeIcon },
    { id: 'dim', label: 'Dimension', icon: Ruler },
    { id: 'erase', label: 'Delete', icon: Eraser },
];

export default function Sketch2D({ onExit, onOpenSettings, email, onLogout }) {
    const canvasRef = useRef(null);
    const [tool, setTool] = useState('select');
    const [entities, setEntities] = useState([]);
    const [draft, setDraft] = useState(null);
    const [snap, setSnap] = useState(true);
    const [showGrid, setShowGrid] = useState(true);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [cursor, setCursor] = useState({ x: 0, y: 0 });
    const [selected, setSelected] = useState(null);
    const undoStack = useRef([]); const redoStack = useRef([]);
    const [, force] = useState(0);
    const t = { bg: '#0f172a', bgSub: '#1e293b', text: '#e2e8f0', textMuted: '#94a3b8', borderLight: '#334155' };
    const GRID = 25;

    const worldFromScreen = useCallback((sx, sy) => {
        const rect = canvasRef.current.getBoundingClientRect();
        let x = (sx - rect.left - pan.x) / zoom / GRID;
        let y = -(sy - rect.top - pan.y) / zoom / GRID;
        if (snap) { x = Math.round(x); y = Math.round(y); }
        return { x, y };
    }, [pan, zoom, snap]);

    const pushUndo = () => { undoStack.current.push(JSON.stringify(entities)); if (undoStack.current.length > 50) undoStack.current.shift(); redoStack.current = []; force((n) => n + 1); };
    const doUndo = () => { const prev = undoStack.current.pop(); if (prev) { redoStack.current.push(JSON.stringify(entities)); setEntities(JSON.parse(prev)); setSelected(null); force((n) => n + 1); } };
    const doRedo = () => { const nxt = redoStack.current.pop(); if (nxt) { undoStack.current.push(JSON.stringify(entities)); setEntities(JSON.parse(nxt)); setSelected(null); force((n) => n + 1); } };

    const redraw = useCallback(() => {
        const cv = canvasRef.current; if (!cv) return;
        const ctx = cv.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const w = cv.clientWidth, h = cv.clientHeight;
        if (cv.width !== Math.round(w * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = '#0b1220'; ctx.fillRect(0, 0, w, h);
        if (showGrid) {
            ctx.strokeStyle = 'rgba(51,65,85,0.5)'; ctx.lineWidth = 1;
            const step = GRID * zoom;
            for (let x = (pan.x % step); x < w; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
            for (let y = (pan.y % step); y < h; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
        }
        ctx.strokeStyle = 'rgba(96,165,250,0.5)';
        ctx.beginPath(); ctx.moveTo(pan.x, 0); ctx.lineTo(pan.x, h); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, pan.y); ctx.lineTo(w, pan.y); ctx.stroke();
        const sx = (wx) => wx * GRID * zoom + pan.x;
        const sy = (wy) => -wy * GRID * zoom + pan.y;
        const drawEnt = (e, isSel) => {
            ctx.strokeStyle = isSel ? '#f59e0b' : (e.color || '#60a5fa');
            ctx.fillStyle = isSel ? '#f59e0b' : '#cbd5e1';
            ctx.lineWidth = isSel ? 2.5 : 1.5;
            if (e.type === 'text') { ctx.font = `${Math.max(11, 14 * zoom)}px sans-serif`; ctx.fillText(e.text, sx(e.x1), sy(e.y1)); return; }
            ctx.beginPath();
            if (e.type === 'line' || e.type === 'dim') { ctx.moveTo(sx(e.x1), sy(e.y1)); ctx.lineTo(sx(e.x2), sy(e.y2)); }
            else if (e.type === 'rect') { ctx.rect(sx(Math.min(e.x1, e.x2)), sy(Math.max(e.y1, e.y2)), Math.abs(e.x2 - e.x1) * GRID * zoom, Math.abs(e.y2 - e.y1) * GRID * zoom); }
            else if (e.type === 'circle') { ctx.arc(sx(e.x1), sy(e.y1), Math.hypot(e.x2 - e.x1, e.y2 - e.y1) * GRID * zoom, 0, Math.PI * 2); }
            ctx.stroke();
            if (e.type === 'dim') {
                const mx = (sx(e.x1) + sx(e.x2)) / 2, my = (sy(e.y1) + sy(e.y2)) / 2 - 8;
                ctx.fillStyle = '#f59e0b'; ctx.font = '11px monospace';
                ctx.fillText(Math.hypot(e.x2 - e.x1, e.y2 - e.y1).toFixed(2) + ' u', mx, my);
            }
        };
        entities.forEach((e, i) => drawEnt(e, i === selected));
        if (draft) { ctx.globalAlpha = 0.6; drawEnt(draft, false); ctx.globalAlpha = 1; }
    }, [entities, draft, pan, zoom, showGrid, selected]);

    useEffect(() => {
        const cv = canvasRef.current;
        const ro = new ResizeObserver(redraw); ro.observe(cv); redraw();
        return () => ro.disconnect();
    }, [redraw]);

    const hitTest = (p) => entities.findIndex((en) => {
        if (en.type === 'line' || en.type === 'dim') return Math.hypot(p.x - en.x1, p.y - en.y1) < 0.4 || Math.hypot(p.x - en.x2, p.y - en.y2) < 0.4 || pointNearSeg(p, en) < 0.25;
        if (en.type === 'rect') return p.x >= Math.min(en.x1, en.x2) - 0.15 && p.x <= Math.max(en.x1, en.x2) + 0.15 && p.y >= Math.min(en.y1, en.y2) - 0.15 && p.y <= Math.max(en.y1, en.y2) + 0.15;
        if (en.type === 'circle') return Math.abs(Math.hypot(p.x - en.x1, p.y - en.y1) - Math.hypot(en.x2 - en.x1, en.y2 - en.y1)) < 0.3;
        if (en.type === 'text') return Math.hypot(p.x - en.x1, p.y - en.y1) < 1;
        return false;
    });
    const pointNearSeg = (p, e) => {
        const dx = e.x2 - e.x1, dy = e.y2 - e.y1; const L2 = dx * dx + dy * dy;
        if (L2 === 0) return Math.hypot(p.x - e.x1, p.y - e.y1);
        let tt = ((p.x - e.x1) * dx + (p.y - e.y1) * dy) / L2; tt = Math.max(0, Math.min(1, tt));
        return Math.hypot(p.x - (e.x1 + tt * dx), p.y - (e.y1 + tt * dy));
    };

    const onDown = (e) => {
        if (e.button === 1 || (e.button === 2 && tool === 'select')) { panDrag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; return; }
        const p = worldFromScreen(e.clientX, e.clientY);
        if (tool === 'select') { const hit = hitTest(p); setSelected(hit >= 0 ? hit : null); return; }
        if (tool === 'erase') { const hit = hitTest(p); if (hit >= 0) { pushUndo(); setEntities(entities.filter((_, i) => i !== hit)); setSelected(null); } return; }
        if (tool === 'text') { const txt = prompt('Enter text:'); if (txt) { pushUndo(); setEntities([...entities, { type: 'text', x1: p.x, y1: p.y, text: txt }]); } return; }
        setDraft({ type: tool === 'poly' ? 'line' : tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    };
    const panDrag = useRef(null);
    const onMove = (e) => {
        const p = worldFromScreen(e.clientX, e.clientY);
        setCursor(p);
        if (panDrag.current) { setPan({ x: e.clientX - panDrag.current.x, y: e.clientY - panDrag.current.y }); return; }
        if (draft) setDraft({ ...draft, x2: p.x, y2: p.y });
    };
    const onUp = () => {
        panDrag.current = null;
        if (draft) {
            if (Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) > 0.2) { pushUndo(); setEntities([...entities, draft]); }
            setDraft(null);
        }
    };
    const onWheel = (e) => { e.preventDefault(); setZoom((z) => Math.min(6, Math.max(0.2, z * (e.deltaY < 0 ? 1.12 : 0.89)))); };

    const exportDXF = () => {
        let d = '0\nSECTION\n2\nENTITIES\n';
        entities.forEach((en) => {
            if (en.type === 'line' || en.type === 'dim') d += `0\nLINE\n8\n0\n10\n${en.x1}\n20\n${en.y1}\n11\n${en.x2}\n21\n${en.y2}\n`;
            else if (en.type === 'circle') d += `0\nCIRCLE\n8\n0\n10\n${en.x1}\n20\n${en.y1}\n40\n${Math.hypot(en.x2 - en.x1, en.y2 - en.y1)}\n`;
        });
        d += '0\nENDSEC\n0\nEOF\n';
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([d], { type: 'application/dxf' }));
        a.download = 'roocad-sketch.dxf'; a.click();
    };

    const active = selected !== null && selected >= 0 ? entities[selected] : null;

    return (
        <div style={{ display: 'flex', height: '100%', position: 'relative', fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ width: 52, background: t.bgSub, borderRight: `1px solid ${t.borderLight}`, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0', gap: 3 }}>
                <button onClick={onExit} title="Back to 3D" style={{ marginBottom: 8, padding: 9, borderRadius: 9, border: 'none', cursor: 'pointer', background: '#2563eb', color: '#fff', display: 'flex' }}><ChevronLeft size={16} /></button>
                {TOOLS.map(({ id, label, icon: Icon }) => (
                    <button key={id} title={label} onClick={() => { setTool(id); setSelected(null); }} style={{ padding: 9, borderRadius: 9, border: 'none', cursor: 'pointer', display: 'flex', background: tool === id ? '#2563eb' : 'transparent', color: tool === id ? '#fff' : t.textMuted }}><Icon size={16} /></button>
                ))}
                <span style={{ flex: 1 }} />
                <button title='Grid' onClick={() => setShowGrid(!showGrid)} style={railBtn(showGrid)}><Magnet size={0} style={{display:'none'}} /><Layers size={16} /></button>
                <button title='Snap' onClick={() => setSnap(!snap)} style={railBtn(snap)}><Magnet size={16} /></button>
                <button title='Undo' onClick={doUndo} style={railBtn(false)}><Undo2 size={16} /></button>
                <button title='Redo' onClick={doRedo} style={railBtn(false)}><Redo2 size={16} /></button>
                <button title='Clear all' onClick={() => { if (entities.length) { pushUndo(); setEntities([]); setSelected(null); } }} style={railBtn(false)}><Trash2 size={16} /></button>
                <button title='Export DXF' onClick={exportDXF} style={railBtn(false)}><Download size={16} /></button>
            </div>
            <div style={{ flex: 1, position: 'relative', background: t.bg }}>
                <div style={{ position: 'absolute', top: 10, left: 12, zIndex: 5, display: 'flex', alignItems: 'center', gap: 8, background: t.bgSub, border: `1px solid ${t.borderLight}`, borderRadius: 10, padding: '6px 12px', fontSize: 12, fontWeight: 700, color: t.text }}>
                    <SplineIcon size={13} color='#60a5fa' /> 2D Sketch
                </div>
                <canvas ref={canvasRef}
                    style={{ width: '100%', height: '100%', display: 'block', cursor: tool === 'select' ? 'default' : 'crosshair' }}
                    onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
                    onWheel={onWheel} onContextMenu={(e) => e.preventDefault()} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 28, display: 'flex', alignItems: 'center', gap: 18, padding: '0 14px', background: t.bgSub, borderTop: `1px solid ${t.borderLight}`, fontSize: 11, color: t.textMuted, fontFamily: 'monospace' }}>
                    <span>X {cursor.x.toFixed(2)}  Y {cursor.y.toFixed(2)}</span>
                    <span>Zoom {(zoom * 100).toFixed(0)}%</span>
                    <span>Snap {snap ? 'ON' : 'OFF'}</span>
                    <span>{entities.length} entities</span>
                    {active && <span style={{ color: '#f59e0b' }}>selected: {active.type}</span>}
                    <span style={{ flex: 1 }} />
                    <Layers size={11} /> Layer 0
                </div>
                {active && (
                    <div style={{ position: 'absolute', top: 54, right: 12, width: 190, background: t.bgSub, border: `1px solid ${t.borderLight}`, borderRadius: 12, padding: 14, fontSize: 12 }}>
                        <div style={{ fontWeight: 700, marginBottom: 8, color: t.text, textTransform: 'capitalize' }}>{active.type} properties</div>
                        {['x1', 'y1', 'x2', 'y2'].filter((k) => active[k] !== undefined).map((k) => (
                            <label key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, color: t.textMuted }}>
                                {k}
                                <input type='number' step='0.1' value={active[k]}
                                    onChange={(ev) => { const v = parseFloat(ev.target.value) || 0; setEntities(entities.map((en, i) => i === selected ? { ...en, [k]: v } : en)); }}
                                    style={{ width: 72, padding: '4px 6px', background: '#0b1220', border: `1px solid ${t.borderLight}`, borderRadius: 6, color: t.text, fontFamily: 'monospace' }} />
                            </label>
                        ))}
                        {active.type === 'text' && (
                            <input value={active.text} onChange={(ev) => setEntities(entities.map((en, i) => i === selected ? { ...en, text: ev.target.value } : en))} style={{ width: '100%', marginBottom: 6, padding: 5, background: '#0b1220', border: `1px solid ${t.borderLight}`, borderRadius: 6, color: t.text, fontSize: 12, boxSizing: 'border-box' }} />
                        )}
                        <button onClick={() => { pushUndo(); setEntities(entities.filter((_, i) => i !== selected)); setSelected(null); }} style={{ width: '100%', marginTop: 4, padding: 7, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Delete</button>
                    </div>
                )}
                <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
                    <button onClick={onOpenSettings} style={topBtn}><Settings size={14} /></button>
                    <button onClick={onLogout} title={email} style={topBtn}><LogOut size={14} /></button>
                </div>
            </div>
        </div>
    );
}

function railBtn(on) { return { padding: 9, borderRadius: 9, border: 'none', cursor: 'pointer', display: 'flex', background: on ? 'rgba(37,99,235,0.25)' : 'transparent', color: on ? '#60a5fa' : '#94a3b8' }; }
const topBtn = { padding: 8, background: '#1e293b', border: '1px solid #334155', borderRadius: 9, color: '#94a3b8', cursor: 'pointer', display: 'flex' };
